import * as React from "react";
import { Button as MantineButton } from "@mantine/core";
import styled, { useTheme, css } from "styled-components";
import Link from "components/common/Link";
const sizeForMantine = (size) => {
  switch (size) {
    case "xs":
    case "xsmall":
      return "xs";
    case "sm":
    case "small":
      return "sm";
    case "lg":
    case "large":
      return "lg";
    default:
      return "md";
  }
};
const isLinkStyle = (style) => style === "link";
const isTransparentStyle = (style) => style === "transparent";
const mapStyle = (style) => style === "default" ? "gray" : style;
const stylesProps = (style) => {
  switch (style) {
    case "link":
      return { variant: "subtle" };
    default:
      return {};
  }
};
const stylesForSize = (size, bsStyle) => {
  if (isLinkStyle(bsStyle)) {
    return css`
      padding: 0;
      height: auto;
    `;
  }
  switch (size) {
    case "xs":
    case "xsmall":
      return css`
        height: 21.4141px;
        padding: 1px 5px;
      `;
    case "sm":
    case "small":
      return css`
        height: 29.4141px;
        padding: 5px 10px;
      `;
    case "lg":
    case "large":
      return css`
        height: 43.1641px;
        padding: 10px 16px;
      `;
    case "medium":
    default:
      return css`
        height: 33.84px;
        padding: 6px 12px;
      `;
  }
};
const disabledStyles = (themeColors, style) => {
  const isSpecialStyle = isLinkStyle(style) || isTransparentStyle(style);
  const colors = isSpecialStyle ? { color: themeColors.text.primary, background: "transparent" } : themeColors.disabled[style];
  return css`
    &:disabled,
    &[data-disabled] {
      pointer-events: all;
      color: ${colors.color};
      background-color: ${colors.background};
      opacity: 0.45;

      &:hover {
        color: ${colors.color};
        text-decoration: none;
      }
    }
  `;
};
const activeStyles = (themeColors, bsStyle) => {
  switch (bsStyle) {
    case "danger":
    case "info":
    case "success":
    case "primary":
    case "warning":
    case "transparent":
      return css`
        color: ${themeColors.text.primary};

        &:hover {
          color: ${themeColors.text.primary};
        }

        &:focus {
          color: ${themeColors.text.primary};
        }
      `;
    default:
      return "";
  }
};
const linkStyles = css`
  vertical-align: baseline;
  user-select: text;

  &:hover {
    background: transparent;
    text-decoration: underline;
  }

  .mantine-Button-label {
    word-break: break-word;
    white-space: normal;
    text-align: left;
  }
`;
const transparentStyles = css`
  &:hover {
    background: transparent;
  }
`;
const textColor = (style, colors) => {
  switch (style) {
    case "link":
      return colors.global.link;
    case "transparent":
      return colors.text.primary;
    default:
      return colors.button[style].color;
  }
};
const StyledButton = styled(MantineButton)(({ theme, $bsStyle, $bsSize, $active }) => {
  const isLink = isLinkStyle($bsStyle);
  const isTransparent = isTransparentStyle($bsStyle);
  const color = textColor($bsStyle, theme.colors);
  return css`
    color: ${color};
    font-weight: 400;
    overflow: visible;

    ${disabledStyles(theme.colors, $bsStyle)}
    ${stylesForSize($bsSize, $bsStyle)}

    &:hover {
      color: ${isLink ? theme.colors.global.linkHover : color};
      text-decoration: none;
    }

    &:focus {
      color: ${color};
      text-decoration: none;
    }

    ${$active && activeStyles(theme.colors, $bsStyle)}
    ${isLink && linkStyles}
    ${isTransparent && transparentStyles}

    .mantine-Button-label {
      gap: 0.25em;
      overflow: visible;
    }

    .mantine-Button-loader {
      visibility: hidden;
    }
  `;
});
const Button = ({
  "aria-label": ariaLabel,
  bsStyle = "default",
  bsSize = void 0,
  className = void 0,
  "data-testid": dataTestId,
  id = void 0,
  onClick = void 0,
  disabled = false,
  href = void 0,
  title = void 0,
  form = void 0,
  target = void 0,
  type = void 0,
  rel = void 0,
  role = void 0,
  name = void 0,
  tabIndex = void 0,
  children = void 0,
  active = void 0
}, ref) => {
  const theme = useTheme();
  const style = mapStyle(bsStyle);
  const color = isLinkStyle(style) || isTransparentStyle(style) ? "transparent" : theme.colors.button[style].background;
  const sharedProps = {
    id,
    "aria-label": ariaLabel,
    className,
    ...stylesProps(style),
    $active: active,
    $bsStyle: style,
    $bsSize: bsSize,
    variant: active ? "outline" : "filled",
    color,
    "data-testid": dataTestId,
    disabled,
    role,
    size: sizeForMantine(bsSize),
    tabIndex,
    title,
    type
  };
  if (href) {
    return /* @__PURE__ */ React.createElement(
      StyledButton,
      {
        component: Link,
        to: href,
        target,
        rel,
        onClick,
        ...sharedProps
      },
      children
    );
  }
  return /* @__PURE__ */ React.createElement(
    StyledButton,
    {
      ref,
      form,
      onClick,
      name,
      ...sharedProps
    },
    children
  );
};
export default React.forwardRef(Button);
